package com.ijoin.ui.tickets

import com.ijoin.data.model.Ticket
import com.ijoin.data.model.TicketStatus
import com.ijoin.data.repository.TicketRepository
import com.ijoin.i18n.Strings
import com.ijoin.i18n.globalStrings
import com.ijoin.ui.util.userFriendlyErrorMessage
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class TicketUiState(
    val isLoading: Boolean = true,
    val ticket: Ticket? = null,
    val error: String? = null,
    val isCancelling: Boolean = false,
    val cancelError: String? = null,
    val cancellationSuccess: Boolean = false,
)

class TicketViewModel(
    private val ticketId: String,
    private val ticketRepository: TicketRepository = TicketRepository(),
) {

    private val viewModelScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
    private val strings: Strings
        get() = globalStrings()

    private val _uiState = MutableStateFlow(TicketUiState())
    val uiState: StateFlow<TicketUiState> = _uiState.asStateFlow()

    init {
        loadTicket()
    }

    fun loadTicket() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            ticketRepository.getTicketById(ticketId)
                .onSuccess { ticket ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            ticket = ticket,
                            error = null,
                            cancellationSuccess = false,
                            cancelError = null,
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = userFriendlyErrorMessage(error, strings.ticketLoadError),
                        )
                    }
                }
        }
    }

    fun cancelTicket() {
        val currentTicket = _uiState.value.ticket ?: return
        if (currentTicket.status != TicketStatus.ACTIVE || _uiState.value.isCancelling) {
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isCancelling = true, cancelError = null, cancellationSuccess = false) }
            ticketRepository.cancelTicket(ticketId)
                .onSuccess { cancelled ->
                    _uiState.update {
                        it.copy(
                            ticket = cancelled,
                            isCancelling = false,
                            cancellationSuccess = true,
                            cancelError = null,
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isCancelling = false,
                            cancelError = userFriendlyErrorMessage(error, strings.ticketCancellationFailure),
                        )
                    }
                }
        }
    }

    fun consumeMessages() {
        _uiState.update { it.copy(cancellationSuccess = false, cancelError = null) }
    }
}
